import React, { useState } from 'react';
import { useDesignStore } from '../../stores/designStore';

export const VersionHistory: React.FC = () => {
  const {
    versions,
    currentVersionId,
    currentDesign,
    rollbackToVersion,
    deleteVersion,
    addVersion,
    previewHtml,
  } = useDesignStore();

  const [expandedVersion, setExpandedVersion] = useState<string | null>(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState<string | null>(null);

  // 格式化时间
  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    
    // 小于1分钟
    if (diff < 60000) {
      return '刚刚';
    }
    // 小于1小时
    if (diff < 3600000) {
      return `${Math.floor(diff / 60000)}分钟前`;
    }
    // 小于24小时
    if (diff < 86400000) {
      return `${Math.floor(diff / 3600000)}小时前`;
    }
    // 显示日期
    return date.toLocaleDateString('zh-CN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  // 手动保存当前版本
  const handleSaveVersion = () => {
    if (!currentDesign || !previewHtml) return;
    
    addVersion({
      design: currentDesign,
      previewHtml,
      note: '手动保存',
    });
  };

  // 回退到指定版本
  const handleRollback = (versionId: string) => {
    if (rollbackToVersion(versionId)) {
      setExpandedVersion(null);
    }
  };

  // 删除版本
  const handleDelete = (versionId: string) => {
    deleteVersion(versionId);
    setShowConfirmDelete(null);
    setExpandedVersion(null);
  };

  // 对比版本
  const handleCompare = (versionId: string) => {
    // TODO: 实现版本对比功能
    console.log('Compare with version:', versionId);
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* 头部 */}
      <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50">
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-xs font-medium text-gray-700">版本历史</span>
        </div>
        <button
          onClick={handleSaveVersion}
          disabled={!currentDesign}
          className="text-xs px-2 py-1 bg-purple-100 text-purple-600 rounded hover:bg-purple-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          保存当前
        </button>
      </div>

      {/* 版本列表 */}
      <div className="flex-1 overflow-y-auto">
        {versions.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-gray-400 p-4">
            <svg className="w-10 h-10 mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <p className="text-xs text-center">暂无历史版本</p>
            <p className="text-xs text-center text-gray-300 mt-1">设计变更将自动保存</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {versions.slice().reverse().map((version, index) => {
              const isCurrent = version.id === currentVersionId;
              const isExpanded = expandedVersion === version.id;
              const versionNumber = versions.length - index;

              return (
                <div
                  key={version.id}
                  className={`
                    group relative
                    ${isCurrent ? 'bg-purple-50' : 'hover:bg-gray-50'}
                  `}
                >
                  {/* 版本项头部 */}
                  <button
                    onClick={() => setExpandedVersion(isExpanded ? null : version.id)}
                    className="w-full px-3 py-2.5 text-left"
                  >
                    <div className="flex items-start gap-2">
                      {/* 版本号 */}
                      <div className={`
                        w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0
                        ${isCurrent 
                          ? 'bg-purple-500 text-white' 
                          : 'bg-gray-200 text-gray-600 group-hover:bg-gray-300'
                        }
                      `}>
                        {versionNumber}
                      </div>

                      {/* 版本信息 */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-medium text-gray-800 truncate">
                            {version.note || `版本 ${versionNumber}`}
                          </span>
                          {isCurrent && (
                            <span className="text-xs px-1.5 py-0.5 bg-purple-100 text-purple-600 rounded">
                              当前
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {formatTime(version.timestamp)}
                        </p>
                      </div>

                      {/* 展开图标 */}
                      <svg 
                        className={`
                          w-4 h-4 text-gray-400 transition-transform
                          ${isExpanded ? 'rotate-180' : ''}
                        `} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>

                  {/* 展开的操作菜单 */}
                  {isExpanded && (
                    <div className="px-3 pb-3 pl-11">
                      <div className="flex flex-wrap gap-2">
                        {!isCurrent && (
                          <button
                            onClick={() => handleRollback(version.id)}
                            className="text-xs px-2 py-1 bg-purple-100 text-purple-600 rounded hover:bg-purple-200 transition-colors"
                          >
                            回退到此版本
                          </button>
                        )}
                        <button
                          onClick={() => handleCompare(version.id)}
                          className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition-colors"
                        >
                          对比
                        </button>
                        <button
                          onClick={() => setShowConfirmDelete(version.id)}
                          className="text-xs px-2 py-1 bg-red-50 text-red-600 rounded hover:bg-red-100 transition-colors"
                        >
                          删除
                        </button>
                      </div>

                      {/* 删除确认 */}
                      {showConfirmDelete === version.id && (
                        <div className="mt-2 p-2 bg-red-50 rounded text-xs">
                          <p className="text-red-600 mb-2">确定删除此版本？</p>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleDelete(version.id)}
                              className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                            >
                              确定
                            </button>
                            <button
                              onClick={() => setShowConfirmDelete(null)}
                              className="px-2 py-1 bg-gray-200 text-gray-600 rounded hover:bg-gray-300"
                            >
                              取消
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 底部提示 */}
      {versions.length > 0 && (
        <div className="px-3 py-2 border-t bg-gray-50 text-xs text-gray-400 text-center">
          共 {versions.length} 个版本
        </div>
      )}
    </div>
  );
};
